import { cn } from '@/lib/utils';
import { STRINGS } from '@/strings/strings';

export type BuilderMode = 'single' | 'series';

interface SeriesModeToggleProps {
  value: BuilderMode;
  onChange: (value: BuilderMode) => void;
  /** Override the section-label colour (KP uses white-on-navy). */
  labelClassName?: string;
  /**
   * KP renders on a dark starry-navy background where the default navy-filled
   * active card + faded white inactive card are near-invisible. `dark` swaps
   * to a high-contrast treatment (gold-bordered white active, outlined-white
   * inactive). AA (light bg) keeps the default — pass nothing.
   */
  dark?: boolean;
}

/**
 * Two-card mode toggle (Einzelgeschichte | Serie), mirroring the sb
 * "Erzählform" ModeSelector pattern exactly (grid-cols-2, navy-filled
 * active, .builder-field opacity-50 inactive).
 */
export function SeriesModeToggle({
  value,
  onChange,
  labelClassName,
  dark = false,
}: SeriesModeToggleProps) {
  const S = STRINGS.series.builder;
  return (
    <div className="space-y-1.5">
      <span className={cn('text-sm text-dream-navy', labelClassName)}>{S.modeLabel}</span>
      <div className="grid grid-cols-2 gap-3">
        <ModeCard
          active={value === 'single'}
          onClick={() => onChange('single')}
          label={S.modeSingle}
          description={S.modeSingleSub}
          dark={dark}
        />
        <ModeCard
          active={value === 'series'}
          onClick={() => onChange('series')}
          label={S.modeSeries}
          description={S.modeSeriesSub}
          dark={dark}
        />
      </div>
    </div>
  );
}

interface ModeCardProps {
  active: boolean;
  onClick: () => void;
  label: string;
  description: string;
  dark?: boolean;
}

export function ModeCard({ active, onClick, label, description, dark = false }: ModeCardProps) {
  // KP dark bg: gold-bordered white active (pops on navy), outlined-white
  // inactive (clearly visible). AA light bg: the original navy-fill / faded
  // builder-field treatment, unchanged.
  const surface = dark
    ? active
      ? 'border-2 border-dream-gold bg-dream-white'
      : 'border border-dream-white/40 bg-dream-white/10'
    : active
      ? 'border-[1.5px] border-dream-navy bg-dream-navy'
      : 'builder-field opacity-50';
  const labelColor = dark
    ? active
      ? 'font-bold text-dream-navy'
      : 'text-dream-white'
    : active
      ? 'font-bold text-dream-white'
      : 'text-dream-navy';
  const descColor = dark
    ? active
      ? 'text-dream-navy/70'
      : 'text-dream-white/70'
    : active
      ? 'text-dream-white/80'
      : 'text-dream-navy/70';

  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        'flex min-h-11 flex-col items-start gap-1 rounded-lg p-3 text-left transition-colors',
        surface,
      )}
    >
      <span className={cn('text-base', labelColor)}>{label}</span>
      <span className={cn('text-xs leading-snug', descColor)}>{description}</span>
    </button>
  );
}
